import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  type AgentDetail,
  fetchAgent,
  fetchAgents,
  fetchAgentVersions,
  type SaveAgentLayerInput,
  saveAgentLayer,
  testResolveAgent
} from '@/api/agents.ts'

/**
 * Query hooks for the agent studio.
 *
 * Reads are cached briefly and writes invalidate rather than patch the cache: a save
 * bumps the layer's version, and a hand-patched cache that guesses the new version would
 * hand the next save an `If-Match` the server never issued — which is the one failure this
 * screen exists to prevent.
 */

export const agentKeys = {
  all: ['admin', 'agents'] as const,
  list: ['admin', 'agents', 'list'] as const,
  detail: (agentId: string) => ['admin', 'agents', 'detail', agentId] as const,
  versions: (agentId: string) => ['admin', 'agents', 'versions', agentId] as const,
  resolve: (agentId: string, signature: string) => ['admin', 'agents', 'resolve', agentId, signature] as const
}

export const useAgents = (enabled: boolean) =>
  useQuery({
    queryKey: agentKeys.list,
    queryFn: fetchAgents,
    enabled,
    staleTime: 15_000
  })

export const useAgent = (agentId: string | null) =>
  useQuery<AgentDetail>({
    queryKey: agentKeys.detail(agentId ?? ''),
    queryFn: () => fetchAgent(agentId ?? ''),
    enabled: Boolean(agentId),
    staleTime: 15_000
  })

/** Version history for the definition. Fetched only while the history panel is open. */
export const useAgentVersions = (agentId: string, enabled: boolean) =>
  useQuery({
    queryKey: agentKeys.versions(agentId),
    queryFn: () => fetchAgentVersions(agentId),
    enabled,
    staleTime: 30_000
  })

export const useSaveAgentLayer = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: SaveAgentLayerInput) => saveAgentLayer(input),
    onSuccess: (_layer, input) => {
      void client.invalidateQueries({ queryKey: agentKeys.list })
      void client.invalidateQueries({ queryKey: agentKeys.detail(input.agentId) })
      void client.invalidateQueries({ queryKey: agentKeys.versions(input.agentId) })
    }
  })
}

/**
 * The merged agent a run would drive under — the same for everyone who can use it.
 *
 * A query despite being a POST: `test-resolve` reads. `signature` is the stored definition's
 * version: including it in the key is what makes the preview follow a save without anyone
 * remembering to invalidate it.
 *
 * Not retried: a resolution failure is a verdict about the configuration ("an agent is
 * minimally a system prompt"), not a transient fault, and retrying only delays the answer
 * the operator is waiting for.
 */
export const useResolvedAgent = (agentId: string, signature: string, enabled: boolean) =>
  useQuery({
    queryKey: agentKeys.resolve(agentId, signature),
    queryFn: () => testResolveAgent(agentId),
    enabled,
    retry: false,
    staleTime: 30_000
  })
